import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { useSatuEventTracker } from 'utils/satuEvent'

import type { SETransformAttrsArgs } from 'utils/satuEvent'

type ScreenName =
  | 'KINERJA_KS_BERANDA_PAGE'
  | 'KINERJA_KS_PENILAI_HOMEPAGE'
  | 'KINERJA_KS_PENILAI_DINAS_HOMEPAGE'
  | 'KINERJA_KS_NILAI_RATING_OBSERVASI_POP_UP'
  | 'KINERJA_KS_NILAI_RATING_OBSERVASI_DINAS_POP_UP'
  | 'KINERJA_KS_NILAI_TINDAK_LANJUT_POP_UP'
  | 'KINERJA_KS_NILAI_TINDAK_LANJUT_DINAS_POP_UP'
  | 'KINERJA_KS_NILAI_REFLEKSI_TINDAK_LANJUT_POP_UP'
  | 'KINERJA_KS_NILAI_REFLEKSI_TINDAK_LANJUT_DINAS_POP_UP'
  | 'KINERJA_KS_NILAI_PERILAKU_KERJA_POP_UP'
  | 'KINERJA_KS_NILAI_PERILAKU_KERJA_DINAS_POP_UP'
  | 'KINERJA_KS_MAIN_HUB_PENILAIAN_PAGE'
  | 'KINERJA_KS_MAIN_HUB_PENILAIAN_PAGE'
  | 'KINERJA_KS_DAFTAR_KS_PAGE'
  | 'KINERJA_KS_RINCIAN_PENILAIAN_PAGE'
  | 'KINERJA_KS_PENILAIAN_PRAKTIK_KINERJA_PAGE'

type EventName =
  | 'OPEN_KINERJA_BERANDA'
  | 'OPEN_KINERJA_PENILAI_HOMEPAGE'
  | 'CLICK_KINERJA_SUBMIT_NILAI_RATING_OBSERVASI'
  | 'CLICK_KINERJA_SUBMIT_NILAI_TINDAK_LANJUT'
  | 'CLICK_KINERJA_SUBMIT_NILAI_REFLEKSI_TINDAKLANJUT'
  | 'CLICK_KINERJA_SUBMIT_NILAI_PERILAKU_KERJA'
  | 'OPEN_KINERJA_MAIN_HUB_PENILAIAN'
  | 'CLICK_KINERJA_LAKUKAN_PENILAIAN'
  | 'OPEN_KINERJA_DAFTAR_KEPALA_SEKOLAH'
  | 'CLICK_KINERJA_NILAI_KS'
  | 'OPEN_KINERJA_RINCIAN_PENILAIAN'
  | 'CLICK_KINERJA_NILAI_PRAKTIK_KINERJA'
  | 'CLICK_KINERJA_NILAI_PERILAKU_KERJA'
  | 'CLICK_KINERJA_PERIKSA_DOKUMEN'
  | 'CLICK_KINERJA_CEK_PERENCANAAN'
  | 'CLICK_KINERJA_NILAI_RATING_OBSERVASI'
  | 'CLICK_KINERJA_NILAI_TINDAK_LANJUT'
  | 'CLICK_KINERJA_NILAI_REFLEKSI_TINDAK_LANJUT'

type TrackerParams = {
  screen_name: ScreenName
  event_name: EventName
  attributes?: SETransformAttrsArgs
}

const SATU_EVENT_NAME = 'guru_ks_fe_skp_event_v1'

/**
 * Custom hook for tracking events in the KinerjaPSPage.
 *
 * @returns An object containing functions to track page visits and user clicks.
 */
export const useKinerjaPsTrackEvent = () => {
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()

  const { isKepalaDinasAndPengawasSekolah } = useRoleContext()

  const trackEvent = (params: TrackerParams) => {
    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      eventParams: {
        ...params,
        attributes: SETransformAttrs(params.attributes ?? {}),
        event_trigger: params.screen_name,
      },
      sendUserData: true,
      sendDefaultParams: true,
      sendAttributes: false,
      keepAlive: true,
    })
  }

  const mustHaveAttributes: SETransformAttrsArgs = {
    is_kepala_dinas: [isKepalaDinasAndPengawasSekolah, 'bool'],
  }

  // --------------------------------------------
  // TRACK PAGE VISITS
  // --------------------------------------------
  const trackOpenKinerjaBeranda = () => {
    trackEvent({
      event_name: 'OPEN_KINERJA_BERANDA',
      screen_name: 'KINERJA_KS_BERANDA_PAGE',
    })
  }

  /**
   * Tracks the event when the Pengawas Sekolah's homepage is opened, or when the Kepala Dinas's homepage is opened
   * Path: `/pengelolaan-kinerja/pengawas-sekolah`, `/pengelolaan-kinerja/kepala-dinas`
   */
  const trackOpenKinerjaPenilaianHomepage = (screen_name: ScreenName) => {
    trackEvent({
      event_name: 'OPEN_KINERJA_PENILAI_HOMEPAGE',
      screen_name,
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the event when the Pengawas Sekolah's Penilaian Hub page is opened
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/beranda`
   */
  const trackOpenHub = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_MAIN_HUB_PENILAIAN_PAGE',
      event_name: 'OPEN_KINERJA_MAIN_HUB_PENILAIAN',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the event when the Pengawas Sekolah's List of Kepala Sekolah page is opened
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian`
   */
  const trackOpenDaftarKs = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_DAFTAR_KS_PAGE',
      event_name: 'OPEN_KINERJA_DAFTAR_KEPALA_SEKOLAH',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the event when the Rincian Kepala Sekolah page is opened
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian/{npsn}`
   */
  const trackOpenRincianKs = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_RINCIAN_PENILAIAN_PAGE',
      event_name: 'OPEN_KINERJA_RINCIAN_PENILAIAN',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  // --------------------------------------------
  // TRACK USER CLICKS
  // --------------------------------------------
  const trackClickSubmitNilaiRatingObservasi = (screen_name: ScreenName) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_SUBMIT_NILAI_RATING_OBSERVASI',
      screen_name,
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  const trackClickSubmitNilaiTindakLanjut = (screen_name: ScreenName) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_SUBMIT_NILAI_TINDAK_LANJUT',
      screen_name,
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  const trackClickSubmitNilaiRefleksiTindakLanjut = (
    screen_name: ScreenName
  ) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_SUBMIT_NILAI_REFLEKSI_TINDAKLANJUT',
      screen_name,
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  const trackClickSubmitPerilakuKerja = (screen_name: ScreenName) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_SUBMIT_NILAI_PERILAKU_KERJA',
      screen_name,
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Lakukan Penilaian" button on the Pengawas Sekolah's Penilaian Hub page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/beranda`
   */
  const trackClickLakukanPenilaianOnHub = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_MAIN_HUB_PENILAIAN_PAGE',
      event_name: 'CLICK_KINERJA_LAKUKAN_PENILAIAN',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Nilai" button on the Pengawas Sekolah's List of Kepala Sekolah page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian`
   */
  const trackClickNilaiOnDaftarKs = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_DAFTAR_KS_PAGE',
      event_name: 'CLICK_KINERJA_NILAI_KS',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Nilai" button on Observasi Praktik Kinerja under Rincian Kepala Sekolah page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian/{npsn}`
   */
  const trackClickNilaiPraktikKinerjaOnRincianKs = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_RINCIAN_PENILAIAN_PAGE',
      event_name: 'CLICK_KINERJA_NILAI_PRAKTIK_KINERJA',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Nilai" button on Perilaku Kerja under Rincian Kepala Sekolah page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian/{npsn}`
   */
  const trackClickNilaiPerilakuKerjaOnRincianKs = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_RINCIAN_PENILAIAN_PAGE',
      event_name: 'CLICK_KINERJA_NILAI_PERILAKU_KERJA',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Periksa" button on Kelengkapan Dokumen under Rincian Kepala Sekolah page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian/{npsn}`
   */
  const trackClickPeriksaKelengkapanDokumenOnRincianKs = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_RINCIAN_PENILAIAN_PAGE',
      event_name: 'CLICK_KINERJA_PERIKSA_DOKUMEN',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Cek Perencanaan" button on Rincian Kepala Sekolah page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian/{npsn}`
   */
  const trackClickCekPerencanaanOnRincianKs = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_RINCIAN_PENILAIAN_PAGE',
      event_name: 'CLICK_KINERJA_CEK_PERENCANAAN',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Nilai" button on Rating Observasi under Rincian Praktik Kinerja page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian/{npsn}`/beranda
   */
  const trackClickNilaiRatingObservasiOnRincianPraktikKinerja = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_PENILAIAN_PRAKTIK_KINERJA_PAGE',
      event_name: 'CLICK_KINERJA_NILAI_RATING_OBSERVASI',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Nilai" button on Dokumen Tindak Lanjut under Rincian Praktik Kinerja page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian/{npsn}`/beranda
   */
  const trackClickNilaiDokumenTindakLanjutOnRincianPraktikKinerja = () => {
    trackEvent({
      screen_name: 'KINERJA_KS_PENILAIAN_PRAKTIK_KINERJA_PAGE',
      event_name: 'CLICK_KINERJA_NILAI_TINDAK_LANJUT',
      attributes: {
        ...mustHaveAttributes,
      },
    })
  }

  /**
   * Tracks the click event for the "Nilai" button on Dokumen Refleksi Tindak Lanjut under Rincian Praktik Kinerja page
   * Path: `/pengelolaan-kinerja/pengawas-sekolah/penilaian/{npsn}`/beranda
   */
  const trackClickNilaiDokumenRefleksiTindakLanjutOnRincianPraktikKinerja =
    () => {
      trackEvent({
        screen_name: 'KINERJA_KS_PENILAIAN_PRAKTIK_KINERJA_PAGE',
        event_name: 'CLICK_KINERJA_NILAI_REFLEKSI_TINDAK_LANJUT',
        attributes: {
          ...mustHaveAttributes,
        },
      })
    }

  return {
    // TRACK PAGE VISITS
    trackOpenKinerjaBeranda,
    trackOpenKinerjaPenilaianHomepage,
    trackOpenHub,
    trackOpenDaftarKs,
    trackOpenRincianKs,

    // TRACK USER CLICKS
    trackClickSubmitNilaiRatingObservasi,
    trackClickSubmitNilaiTindakLanjut,
    trackClickSubmitNilaiRefleksiTindakLanjut,
    trackClickSubmitPerilakuKerja,
    trackClickLakukanPenilaianOnHub,
    trackClickNilaiOnDaftarKs,
    trackClickNilaiPraktikKinerjaOnRincianKs,
    trackClickNilaiPerilakuKerjaOnRincianKs,
    trackClickPeriksaKelengkapanDokumenOnRincianKs,
    trackClickCekPerencanaanOnRincianKs,
    trackClickNilaiRatingObservasiOnRincianPraktikKinerja,
    trackClickNilaiDokumenTindakLanjutOnRincianPraktikKinerja,
    trackClickNilaiDokumenRefleksiTindakLanjutOnRincianPraktikKinerja,
  }
}
